import { AbortControllerRegistry } from "./abort-controller-registry.js";
import { throwDestroyedError, throwNoTargetError } from "./errors.js";
import { createState } from "./state.js";
import { isFunction, isNull, isObject, isPromise } from "@videojs/utils/predicate";
//#region src/core/store.ts
const STORE_SYMBOL = Symbol.for("@videojs/store");
const hasOwnProp = Object.prototype.hasOwnProperty;
/** @internal */
function createStore() {
	return ((slice, options = {}) => {
		let target = null;
		let destroyed = false;
		const setupAbort = new AbortController();
		const signals = new AbortControllerRegistry();
		const actions = /* @__PURE__ */ new WeakMap();
		const reportedErrors = /* @__PURE__ */ new WeakSet();
		let sourceState;
		let state;
		function validate() {
			if (destroyed) throwDestroyedError();
			if (!target) throwNoTargetError();
		}
		const initialSourceState = freezeCopy(slice.state({
			target: () => {
				validate();
				return target;
			},
			signals,
			get: () => sourceState,
			set: (partial) => setSource(partial)
		}));
		sourceState = initialSourceState;
		const initialDerivedState = derive(sourceState);
		state = createState(publish(sourceState, initialDerivedState));
		const store = {
			[STORE_SYMBOL]: true,
			get $state() {
				return state;
			},
			get target() {
				return target;
			},
			get destroyed() {
				return destroyed;
			},
			get state() {
				return state.current;
			},
			attach,
			destroy,
			subscribe
		};
		for (const key of Object.keys(state.current)) Object.defineProperty(store, key, {
			get: () => state.current[key],
			enumerable: true
		});
		for (const key of Object.getOwnPropertySymbols(sourceState)) {
			if (typeof sourceState[key] !== "function") continue;
			Object.defineProperty(store, key, { get: () => sourceState[key] });
		}
		try {
			options.onSetup?.({
				store,
				signal: setupAbort.signal
			});
		} catch (error) {
			reportError(error);
		}
		return store;
		function derive(source) {
			const result = {};
			const definitions = slice.derived;
			if (!definitions) return result;
			const ctx = { get: () => source };
			for (const key of Object.keys(definitions)) result[key] = definitions[key](ctx);
			return result;
		}
		function publish(source, derived) {
			const result = {};
			for (const key of Object.keys(source)) result[key] = source[key];
			Object.assign(result, derived);
			for (const key of Object.keys(result)) {
				const value = result[key];
				if (isFunction(value)) result[key] = wrapAction(value);
			}
			return result;
		}
		function wrapAction(action) {
			const cached = actions.get(action);
			if (cached) return cached;
			const wrapped = function(...args) {
				try {
					const result = action.apply(this, args);
					if (options.onError && isPromise(result)) result.catch(reportError);
					return result;
				} catch (error) {
					if (options.onError) reportError(error);
					throw error;
				}
			};
			actions.set(action, wrapped);
			return wrapped;
		}
		function setSource(partial) {
			const patched = patchSource(sourceState, partial);
			if (!patched) return;
			const nextDerived = derive(patched.next);
			sourceState = patched.next;
			state.replace(publish(sourceState, nextDerived));
		}
		function attach(newTarget) {
			if (destroyed) throwDestroyedError();
			signals.reset();
			target = newTarget;
			const attachContext = {
				target: newTarget,
				signal: signals.base,
				get: () => sourceState,
				set: (partial) => {
					try {
						setSource(partial);
					} catch (error) {
						reportError(error);
					}
				},
				reportError,
				store: {
					get state() {
						return state.current;
					},
					subscribe
				}
			};
			try {
				slice.attach?.(attachContext);
			} catch (error) {
				reportError(error);
			}
			try {
				options.onAttach?.({
					store,
					target: newTarget,
					signal: signals.base
				});
			} catch (error) {
				reportError(error);
			}
			return detach;
		}
		function detach() {
			if (isNull(target)) return;
			signals.reset();
			target = null;
			const resetState = { ...initialSourceState };
			for (const key of slice.preserve ?? []) resetState[key] = sourceState[key];
			setSource(resetState);
		}
		function destroy() {
			if (destroyed) return;
			destroyed = true;
			detach();
			setupAbort.abort();
		}
		function subscribe(callback, options) {
			return state.subscribe(callback, options);
		}
		function reportError(error) {
			if (isObject(error)) {
				if (reportedErrors.has(error)) return;
				reportedErrors.add(error);
			}
			if (options.onError) options.onError({
				store,
				error
			});
			else console.error("[vjs-store]", error);
		}
	});
}
function freezeCopy(value) {
	return Object.freeze({ ...value });
}
function patchSource(current, partial) {
	const next = { ...current };
	let changed = false;
	for (const key of Reflect.ownKeys(partial)) {
		if (!hasOwnProp.call(partial, key)) continue;
		const value = partial[key];
		if (Object.is(current[key], value)) continue;
		next[key] = value;
		changed = true;
	}
	return changed ? { next: Object.freeze(next) } : null;
}
/** @internal */
function isStore(value) {
	return isObject(value) && STORE_SYMBOL in value;
}
//#endregion
export { createStore, isStore };
